import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom'; // Import Link for navigation
import BookAssessment from './BookAssessment';


interface CaseStudy {
  stat: string;
  statDetail: string;
  title: string;
  description: string;
  slug?: string;
  practice: 'ai' | 'cloud';
}

const caseStudies: CaseStudy[] = [
  {
    stat: "70%",
    statDetail: "More Development Throughput",
    title: "An Overnight Development Swarm",
    description: "Technical plans become issues. Overnight, agents implement them behind checks they cannot skip and a review session that never saw them write the code. On one project, 85% of the code was agent-generated.",
    slug: "afk-development",
    practice: 'ai'
  },
  {
    stat: "60%",
    statDetail: "Cost Reduction",
    title: "AWS Cost Optimization",
    description: "Enabled a client to achieve a 60% reduction in AWS costs by optimizing their multi-environment infrastructure. We unified services under AWS Organizations, transitioned to on-demand models, and aligned Savings Plans, ensuring sustained cost control.",
    slug: "cost-reduction", // Used for URL path
    practice: 'cloud'
  },
  {
    stat: "99.999%",
    statDetail: "Availability Achieved",
    title: "Enterprise Landing Page Reliability",
    description: "Resolved frequent landing page downtime for a >$5M company, achieving 99.999% availability. We optimized their auto-scaled environment by implementing pre-baked AMIs and AWS CodeDeploy for faster, reliable deployments.",
    slug: "enterprise-reliability", // Used for URL path
    practice: 'cloud'
  }
];

const ProjectsSection = () => {
  const [lead, ...rest] = caseStudies;

  const Stat = ({ study, large }: { study: CaseStudy; large?: boolean }) => (
    <div>
      <div
        className={`font-display font-bold tabular-nums ${large ? 'text-display-lg' : 'text-display-md'} ${
          study.practice === 'ai' ? 'text-accent-400' : 'text-cloud-400'
        }`}
      >
        {study.stat}
      </div>
      <div className="mt-1 font-mono text-eyebrow uppercase text-gray-400">{study.statDetail}</div>
    </div>
  );

  const CaseLink = ({ study }: { study: CaseStudy }) =>
    study.slug ? (
      <Link
        to={`/success-stories/${study.slug}`}
        className="mt-2 inline-flex min-h-[44px] items-center py-2.5 font-medium text-cloud-300 transition-colors duration-150 hover:text-cloud-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400"
      >
        Read case study <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
      </Link>
    ) : null;

  return (
    <div id="projects" className="scroll-mt-20 bg-gray-800 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-measure">
          <h2 className="font-display text-display-md font-bold text-white sm:text-display-lg">
            Case studies
          </h2>
          <p className="mt-4 text-lg text-gray-300">
            Numbers from our AI engineering and AWS engagements.
          </p>
        </div>

        <article className="mt-14 border-t border-gray-700 pt-8 lg:grid lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-4">
            <Stat study={lead} large />
          </div>
          <div className="mt-6 lg:col-span-8 lg:mt-0">
            <h3 className="font-display text-display-sm font-medium text-white">{lead.title}</h3>
            <p className="mt-3 max-w-measure text-lg text-gray-300">{lead.description}</p>
            <CaseLink study={lead} />
          </div>
        </article>

        <div className="mt-12 grid gap-x-10 gap-y-10 md:grid-cols-2">
          {rest.map((study) => (
            <article key={study.title} className="border-t border-gray-700 pt-6">
              <Stat study={study} />
              <h3 className="mt-4 font-display text-lg font-medium text-white">{study.title}</h3>
              <p className="mt-2 text-gray-400">{study.description}</p>
              <CaseLink study={study} />
            </article>
          ))}
        </div>

        <div className="mt-14 border-t border-gray-700 pt-8">
          <BookAssessment placement="case-studies" variant="bare" band="gray-800" />
        </div>
      </div>
    </div>
  );
};

export default ProjectsSection; 